import { useTranslate } from "ra-core";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Kbd } from "./Kbd";
import {
  CREATE_SHORTCUT_ATTR,
  GO_TO_SHORTCUTS,
  isMac,
  NAV_ROW_ATTR,
  OPEN_SHORTCUTS_EVENT,
} from "./shortcuts";

// Tastatursnarveier, som i Linear:
//   C          Ny … (hovedknappen i sidehodet)
//   /          Søk i lista (eller åpne søkepaletten)
//   G + bokstav  Gå til side
//   J / K      Neste / forrige rad i lista, Enter åpner
//   ?          Vis snarveiene
// Cmd/Ctrl+K (søkepaletten) håndteres av CommandPalette.

// Hvor lenge «G» venter på neste bokstav.
const SEQUENCE_TIMEOUT = 1500;

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return true;
  return !!target.closest(
    '[role="combobox"], [role="listbox"], [role="menu"], [contenteditable="true"]',
  );
};

// En åpen dialog, meny eller nedtrekk eier tastaturet. Et ikke-modalt
// panel (kundepanelet på Oversikt) gjør det ikke.
const isOverlayOpen = () =>
  !!document.querySelector(
    '[role="dialog"]:not([aria-modal="false"]), [role="alertdialog"], [role="menu"]:not([hidden]), [role="listbox"]',
  );

const mainContent = () => document.getElementById("main-content");

const focusSearch = () => {
  const main = mainContent();
  const input =
    main?.querySelector<HTMLInputElement>('input[name="q"]') ??
    main?.querySelector<HTMLInputElement>('input[type="search"]');
  if (input) {
    input.focus();
    input.select();
    return;
  }
  // Ingen søkefelt på siden: åpne søkepaletten i stedet.
  window.dispatchEvent(new Event("mimin:open-command"));
};

const moveRowFocus = (direction: 1 | -1) => {
  const rows = Array.from(
    mainContent()?.querySelectorAll<HTMLElement>(`[${NAV_ROW_ATTR}]`) ?? [],
  ).filter((row) => row.offsetParent !== null);
  if (!rows.length) return;
  let current = rows.findIndex(
    (row) =>
      row === document.activeElement || row.contains(document.activeElement),
  );
  // Fokus utenfor lista (for eksempel i kundepanelet): fortsett fra den
  // valgte raden.
  if (current === -1) {
    current = rows.findIndex(
      (row) => row.getAttribute("aria-current") === "true",
    );
  }
  const next =
    current === -1
      ? direction === 1
        ? 0
        : rows.length - 1
      : Math.min(Math.max(current + direction, 0), rows.length - 1);
  rows[next].focus();
  rows[next].scrollIntoView({ block: "nearest" });
};

export const KeyboardShortcuts = () => {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const pendingG = useRef<number | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey) return;
      if (event.altKey) return;
      if (isTypingTarget(event.target) || isOverlayOpen()) {
        pendingG.current = null;
        return;
      }

      const key = event.key.toLowerCase();

      if (pendingG.current != null) {
        const fresh = Date.now() - pendingG.current < SEQUENCE_TIMEOUT;
        pendingG.current = null;
        const target = GO_TO_SHORTCUTS.find((s) => s.key === key);
        if (fresh && target) {
          event.preventDefault();
          navigate(target.to);
          return;
        }
      }

      switch (event.key) {
        case "?":
          event.preventDefault();
          setHelpOpen(true);
          return;
        case "/":
          event.preventDefault();
          focusSearch();
          return;
      }

      if (event.shiftKey) return;

      switch (key) {
        case "g":
          pendingG.current = Date.now();
          return;
        case "c": {
          const create = mainContent()?.querySelector<HTMLElement>(
            `[${CREATE_SHORTCUT_ATTR}]`,
          );
          if (create) {
            event.preventDefault();
            create.click();
          }
          return;
        }
        case "j":
          event.preventDefault();
          moveRowFocus(1);
          return;
        case "k":
          event.preventDefault();
          moveRowFocus(-1);
          return;
      }
    };
    const openHelp = () => setHelpOpen(true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener(OPEN_SHORTCUTS_EVENT, openHelp);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener(OPEN_SHORTCUTS_EVENT, openHelp);
    };
  }, [navigate]);

  return <ShortcutsHelp open={helpOpen} onOpenChange={setHelpOpen} />;
};

const ShortcutsHelp = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const t = (key: string, fallback: string) => translate(key, { _: fallback });

  const general: [ReactNode, string][] = [
    [
      <>
        <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
        <Kbd>K</Kbd>
      </>,
      t("crm.shortcuts.command", "Søk og naviger"),
    ],
    [<Kbd>/</Kbd>, t("crm.shortcuts.search", "Søk i lista")],
    [<Kbd>C</Kbd>, t("crm.shortcuts.create", "Ny … (hovedknappen på siden)")],
    [<Kbd>?</Kbd>, t("crm.shortcuts.help", "Vis snarveiene")],
  ];
  const lists: [ReactNode, string][] = [
    [
      <>
        <Kbd>J</Kbd>
        <Kbd>K</Kbd>
      </>,
      t("crm.shortcuts.rows", "Neste / forrige rad"),
    ],
    [<Kbd>Enter</Kbd>, t("crm.shortcuts.open", "Åpne raden")],
  ];
  // Kortet i fokus på pipelinetavla. Tastene håndteres av tavla selv, ikke
  // her: de gjelder bare når et kort har fokus.
  const pipeline: [ReactNode, string][] = [
    [<Kbd>Enter</Kbd>, t("crm.shortcuts.pipeline_open", "Åpne mulighet")],
    [<Kbd>A</Kbd>, t("crm.shortcuts.pipeline_next_step", "Neste steg")],
    [
      <>
        <Kbd>Shift</Kbd>
        <Kbd>→</Kbd>
        <Kbd>←</Kbd>
      </>,
      t("crm.shortcuts.pipeline_move", "Neste / forrige stadium"),
    ],
    [<Kbd>V</Kbd>, t("crm.shortcuts.pipeline_won", "Vunnet")],
    [<Kbd>T</Kbd>, t("crm.shortcuts.pipeline_lost", "Tapt")],
    [
      <>
        <Kbd>Q</Kbd>
        <Kbd>D</Kbd>
      </>,
      t(
        "crm.shortcuts.pipeline_qualify",
        "Kvalifiser / diskvalifiser (Lead inn)",
      ),
    ],
    [<Kbd>O</Kbd>, t("crm.shortcuts.pipeline_owner", "Bytt eier")],
    [<Kbd>M</Kbd>, t("crm.shortcuts.pipeline_note", "Notat")],
    [
      <>
        <Kbd>1</Kbd>
        <Kbd>2</Kbd>
        <Kbd>3</Kbd>
      </>,
      t("crm.shortcuts.pipeline_tabs", "Faner: Åpne · Vunnet · Tapt"),
    ],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Fire grupper blir høyere enn en liten skjerm: rull i dialogen. */}
      <DialogContent className="max-h-[90dvh] max-w-md overflow-y-auto">
        <DialogTitle>
          {t("crm.shortcuts.title", "Tastatursnarveier")}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {t(
            "crm.shortcuts.description",
            "Snarveiene virker når du ikke skriver i et felt.",
          )}
        </DialogDescription>
        <div className="grid gap-5 text-sm">
          <ShortcutGroup
            title={t("crm.shortcuts.group_general", "Generelt")}
            rows={general}
          />
          <ShortcutGroup
            title={t("crm.shortcuts.group_go_to", "Gå til")}
            rows={GO_TO_SHORTCUTS.map((s) => [
              <>
                <Kbd>G</Kbd>
                <Kbd>{s.key.toUpperCase()}</Kbd>
              </>,
              translate(s.labelKey, { smart_count: 2, _: s.fallback }),
            ])}
          />
          <ShortcutGroup
            title={t("crm.shortcuts.group_lists", "Lister")}
            rows={lists}
          />
          <ShortcutGroup
            title={t("crm.shortcuts.group_pipeline", "Pipeline (kort i fokus)")}
            rows={pipeline}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ShortcutGroup = ({
  title,
  rows,
}: {
  title: string;
  rows: [ReactNode, string][];
}) => (
  <section>
    <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {title}
    </h3>
    <dl className="grid gap-1.5">
      {rows.map(([keys, label]) => (
        <div key={label} className="flex items-center justify-between gap-4">
          <dt>{label}</dt>
          <dd className="flex shrink-0 gap-1">{keys}</dd>
        </div>
      ))}
    </dl>
  </section>
);
